// OnbInviteWelcome — redesign Phase 8a, "Welcome to [church]'s community".
//
// Shown before InviteAcceptScreen when someone opens an invite link: church
// card (logo, name, meeting info, welcome message, face stack + member
// count), three fixed benefit lines, "Join [church]" / "Not your church?".
//
// Built to docs/designs/OnbInvite.dc.html.
//
// Props:
//   token             — the invite token (same one InviteAcceptScreen uses)
//   onJoin(invite)     — "Join [church]" tapped; parent shows InviteAcceptScreen
//   onNotYourChurch()  — routes to the no-invite "find your church" path

import { useEffect, useState } from "react";
import { supabase } from "../supabase";
import FaceStack from "./FaceStack";

const SERIF = "'Source Serif 4', Georgia, serif";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

const BENEFITS = [
  { dot: "var(--lavender)", text: "Sunday's sermon, all week long" },
  { dot: "var(--gold)",     text: "A 5-minute daily devotional" },
  { dot: "var(--blush)",    text: "Pray for each other, see what's on" },
];

const DEFAULT_WELCOME = "We're so glad you're here. Read along with Sunday's sermon all week, and pray with us.";

export default function OnbInviteWelcome({ token, onJoin, onNotYourChurch }) {
  const [invite, setInvite] = useState(null);   // { churchId, church: {...} }
  const [memberCount, setMemberCount] = useState(0);
  const [faceIds, setFaceIds] = useState([]);
  const [facesById, setFacesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      // get_invitation(p_token) is a SECURITY DEFINER RPC — the raw
      // `invitations` table has no public SELECT policy (see
      // supabase-schema-security-invitations.sql).
      const { data: rows } = await supabase.rpc("get_invitation", { p_token: token });
      const row = Array.isArray(rows) ? rows[0] : rows;
      if (cancelled) return;
      if (!row) { setLoading(false); return; }
      // meeting_info/welcome_message/welcome_from aren't returned by
      // get_invitation() yet — see supabase-schema-security-invitations.sql.
      const data = {
        email: row.email, role: row.role, church_id: row.church_id,
        churches: {
          id: row.church_id, name: row.church_name, logo_url: row.church_logo_url,
          location: row.church_location,
        },
      };
      setInvite(data);
      const churchId = data.church_id;

      const [{ count }, { data: sample }] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("church_id", churchId),
        supabase.from("profiles").select("id, full_name, avatar_url").eq("church_id", churchId).limit(4),
      ]);
      if (cancelled) return;
      setMemberCount(count || 0);
      setFaceIds((sample || []).map(p => p.id));
      setFacesById(Object.fromEntries((sample || []).map(p => [p.id, { full_name: p.full_name, avatar_url: p.avatar_url }])));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div style={S.wrap}><div style={S.loading}>Loading…</div></div>;
  }

  const church = invite?.churches;
  if (!church) {
    // Invalid/expired token — nothing sensible to show; let the parent's
    // existing InviteAcceptScreen surface its own "invite not found" state.
    onJoin?.(invite);
    return null;
  }

  const initials = (church.name || "?").split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const metaLine = [church.location, church.meeting_info].filter(Boolean).join(" · ");

  return (
    <div style={S.wrap}>
      <div style={S.scroll}>
        <svg style={S.logoSmall} viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="62" r="19" fill="#E8C87A"/>
          <path d="M50 58 L50 92 L18 92 Q14 92 14 86 L14 64 Q14 58 20 58 Z" fill="#1F1B16"/>
          <path d="M50 58 L50 92 L82 92 Q86 92 86 86 L86 64 Q86 58 80 58 Z" fill="#3A332C"/>
        </svg>
        <div style={S.label}>YOU'VE BEEN INVITED</div>
        <h1 style={S.heading}>Welcome to {church.name}'s community</h1>

        <div style={S.card}>
          <div style={S.cardHeadRow}>
            {church.logo_url && !logoFailed ? (
              <img src={church.logo_url} alt="" style={S.logo} onError={()=>setLogoFailed(true)}/>
            ) : (
              <div style={S.logoFallback}>{initials}</div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={S.churchName}>{church.name}</div>
              {metaLine && <div style={S.churchMeta}>{metaLine}</div>}
            </div>
          </div>

          <div style={S.welcomeTxt}>"{church.welcome_message || DEFAULT_WELCOME}"</div>
          {church.welcome_from && <div style={S.fromTxt}>{church.welcome_from}</div>}

          {memberCount > 0 && (
            <div style={S.faceRow}>
              <FaceStack userIds={faceIds} profilesById={facesById} size={34}/>
              <span style={S.faceCount}>{memberCount} people from {church.name} are here</span>
            </div>
          )}
        </div>

        <div style={S.benefits}>
          {BENEFITS.map(b => (
            <div key={b.text} style={S.benefitRow}>
              <span style={{ ...S.dot, background: b.dot }}/>
              <span style={S.benefitTxt}>{b.text}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={S.footer}>
        <button style={S.joinBtn} onClick={() => onJoin?.(invite)}>Join {church.name}</button>
        <button style={S.notYoursBtn} onClick={onNotYourChurch}>Not your church?</button>
      </div>
    </div>
  );
}

const S = {
  wrap: { position: "fixed", inset: 0, zIndex: 100, background: "#F6F2EC", display: "flex", flexDirection: "column", fontFamily: SANS },
  loading: { margin: "auto", color: "#6B6258", fontSize: 14 },
  scroll: { flex: 1, overflowY: "auto", padding: "60px 26px 0", display: "flex", flexDirection: "column" },

  logoSmall: { width: 44, height: 44, marginBottom: 14 },
  label: { fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7A5A12", marginBottom: 6 },
  heading: { fontFamily: SANS, fontSize: 33, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.12, margin: "0 0 20px", color: "#1F1B16" },

  card: { background: "#FFFFFF", borderRadius: 28, padding: 20, display: "flex", flexDirection: "column", gap: 16 },
  cardHeadRow: { display: "flex", gap: 14, alignItems: "center" },
  logo: { width: 60, height: 60, borderRadius: 18, objectFit: "cover", flexShrink: 0 },
  logoFallback: { width: 60, height: 60, borderRadius: 18, background: "#E8C87A", color: "#1F1B16", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, flexShrink: 0 },
  churchName: { fontSize: 18, fontWeight: 800, color: "#1F1B16" },
  churchMeta: { fontSize: 13, color: "#6B6258", marginTop: 2 },
  welcomeTxt: { fontFamily: SERIF, fontSize: 17, lineHeight: 1.5, color: "#3D3328" },
  fromTxt: { fontSize: 12.5, color: "#6B6258" },
  faceRow: { display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid #EFE9E0", paddingTop: 12 },
  faceCount: { fontSize: 13, color: "#4A4239" },

  benefits: { display: "flex", flexDirection: "column", gap: 10, marginTop: 22 },
  benefitRow: { display: "flex", alignItems: "center", gap: 10 },
  dot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },
  benefitTxt: { fontSize: 14, color: "#3D3328" },

  footer: { flexShrink: 0, padding: "22px 26px calc(env(safe-area-inset-bottom) + 34px)", display: "flex", flexDirection: "column", gap: 12, alignItems: "center" },
  joinBtn: { width: "100%", background: "#1F1B16", color: "#FFFFFF", border: "none", borderRadius: 999, padding: "17px", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: SANS },
  notYoursBtn: { background: "transparent", border: "none", color: "#7A5A12", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: SANS },
};
